/**
 * One `column · is / is not · values` row, shared by the board's advanced
 * filter and a rule's "only if" conditions. Both read the row through the same
 * matcher, so they are drawn by the same control.
 */
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import {
  CalendarIcon,
  ChevronDownIcon,
  CircleDotIcon,
  ListFilterIcon,
  TriangleAlertIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FILTER_OPERATORS,
  type FilterColumnKind,
  type FilterColumnSpec,
  type FilterOperator,
  type FilterRule,
} from '../../lib/filtering';

const OPERATOR_LABEL_KEY = {
  is: 'trackersBoard.trackersFilterPanel.operatorIs',
  'is-not': 'trackersBoard.trackersFilterPanel.operatorIsNot',
} as const;

const KIND_ICON: Record<FilterColumnKind, ComponentType<{ className?: string }>> = {
  status: CircleDotIcon,
  people: UsersIcon,
  date: CalendarIcon,
  plain: ListFilterIcon,
};

/** What a row edits: a condition as the board filter and a rule's "only if" both write it. */
export type FilterRuleFields = Pick<FilterRule, 'columnId' | 'operator' | 'values'>;

export function FilterRuleRow({
  rule,
  columns,
  label,
  operators = FILTER_OPERATORS,
  onChange,
  onRemove,
}: {
  rule: FilterRuleFields;
  columns: ReadonlyArray<FilterColumnSpec>;
  /** The connector at the head of the row: "Where" first, then "And" or "Or". */
  label: string;
  operators?: readonly FilterOperator[];
  onChange: (patch: Partial<FilterRuleFields>) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation('trackers');

  // A rule can outlive its column: deleting one prunes neither the persisted
  // rules nor the live ones. Falling back to the first column would relabel the
  // rule as a condition it is not, and writing values would then persist that.
  const column = columns.find((c) => c.id === rule.columnId);
  const ColumnIcon = column ? KIND_ICON[column.kind] : TriangleAlertIcon;
  const columnLabel = column?.label ?? t('trackersBoard.trackersFilterPanel.unavailableColumn');

  return (
    <div className="flex w-full items-start gap-2.5">
      <span className="flex h-9 w-13 shrink-0 items-center text-sm font-medium text-foreground">
        {label}
      </span>

      <RulePicker
        ariaLabel={t('trackersBoard.trackersFilterPanel.ruleColumn', { label: columnLabel })}
        icon={<ColumnIcon className="size-4 shrink-0 text-muted-foreground" />}
        label={columnLabel}
        width="w-45"
        options={columns.map((c) => ({ value: c.id, label: c.label }))}
        value={rule.columnId}
        // The values belong to the column that was picked, so they go with it.
        onChange={(columnId) => {
          onChange({ columnId, values: [] });
        }}
      />

      <RulePicker
        ariaLabel={t('trackersBoard.trackersFilterPanel.ruleOperator', {
          label: t(OPERATOR_LABEL_KEY[rule.operator]),
        })}
        label={t(OPERATOR_LABEL_KEY[rule.operator])}
        width="w-32"
        options={operators.map((operator) => ({
          value: operator,
          label: t(OPERATOR_LABEL_KEY[operator]),
        }))}
        value={rule.operator}
        onChange={(operator) => {
          onChange({ operator: operator as FilterOperator });
        }}
      />

      {column ? (
        <RuleValuePicker
          column={column}
          values={rule.values}
          onChange={(values) => {
            onChange({ values });
          }}
        />
      ) : (
        <p className="min-w-0 flex-1 text-sm text-muted-foreground">
          {t('trackersBoard.trackersFilterPanel.unavailableColumnHint')}
        </p>
      )}

      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={t('trackersBoard.trackersFilterPanel.removeFilter')}
        onClick={onRemove}
      >
        <XIcon aria-hidden="true" />
      </Button>
    </div>
  );
}

const PICKER_TRIGGER_CLASS =
  'flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-md border border-border bg-card px-2.5 text-sm text-foreground transition-colors duration-fast ease-out hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';

function RulePicker({
  ariaLabel,
  icon,
  label,
  width,
  options,
  value,
  onChange,
}: {
  ariaLabel: string;
  icon?: ReactNode;
  label: string;
  /** Fixed trigger width, so a row of rules lines its pickers up in columns. */
  width: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label={ariaLabel} className={`${PICKER_TRIGGER_CLASS} ${width}`}>
          {icon}
          <span className="min-w-0 flex-1 truncate text-left">{label}</span>
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        <DropdownMenuRadioGroup value={value} onValueChange={onChange}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function RuleValuePicker({
  column,
  values,
  onChange,
}: {
  column: FilterColumnSpec;
  values: readonly string[];
  onChange: (next: readonly string[]) => void;
}) {
  const { t } = useTranslation('trackers');

  const labelFor = (value: string) => column.options.find((o) => o.value === value)?.label ?? value;

  return (
    <div className="flex min-h-9 min-w-0 flex-1 flex-wrap items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1">
      {values.map((value) => (
        <span
          key={value}
          className="inline-flex h-6 items-center gap-1 rounded-md bg-accent pr-1 pl-2 text-xs text-foreground"
        >
          {labelFor(value)}
          <button
            type="button"
            aria-label={t('trackersBoard.trackersFilterPanel.removeValue', {
              label: labelFor(value),
            })}
            onClick={() => {
              onChange(values.filter((v) => v !== value));
            }}
            className="cursor-pointer text-muted-foreground transition-colors duration-fast ease-out hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <XIcon className="size-3" />
          </button>
        </span>
      ))}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t('trackersBoard.trackersFilterPanel.ruleValues', { label: column.label })}
            className="flex min-w-0 flex-1 cursor-pointer items-center gap-1 px-1 py-0.5 text-left text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            {values.length === 0 && (
              <span className="truncate text-muted-foreground">
                {t('trackersBoard.trackersFilterPanel.selectValues')}
              </span>
            )}
            <ChevronDownIcon className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-55 w-52 overflow-y-auto">
          {column.options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={values.includes(option.value)}
              onCheckedChange={(checked) => {
                onChange(
                  checked ? [...values, option.value] : values.filter((v) => v !== option.value),
                );
              }}
              onSelect={(e) => e.preventDefault()}
            >
              <ValueDot color={option.color} />
              <span className="min-w-0 truncate">{option.label}</span>
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** The 8px swatch a status value is known by; neutral for a column without one. */
export function ValueDot({ color }: { color?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`size-2 shrink-0 rounded-full ${color ? '' : 'bg-border-strong'}`}
      style={color ? { backgroundColor: color } : undefined}
    />
  );
}
